/** Q&A pause window — scheduled duration + live session state. */

export const DEFAULT_QA_DURATION_MINUTES = 10;
export const MIN_QA_DURATION_MINUTES = 5;
export const MAX_QA_DURATION_MINUTES = 60;

export interface SessionQaWindow {
  sessionId: string;
  durationMinutes: number;
  startedAt: number | null;
  endsAt: number | null;
  isActive: boolean;
}

const QA_WINDOW_KEY = 'codelive-session-qa-window';

function dispatchUpdate(): void {
  window.dispatchEvent(new Event('codelive-session-update'));
}

function loadAllQaWindows(): Record<string, SessionQaWindow> {
  try {
    const raw = sessionStorage.getItem(QA_WINDOW_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, SessionQaWindow>;
  } catch {
    return {};
  }
}

function saveAllQaWindows(windows: Record<string, SessionQaWindow>): void {
  sessionStorage.setItem(QA_WINDOW_KEY, JSON.stringify(windows));
  dispatchUpdate();
}

export function clampQaDurationMinutes(minutes: number): number {
  if (!Number.isFinite(minutes)) return DEFAULT_QA_DURATION_MINUTES;
  return Math.min(
    MAX_QA_DURATION_MINUTES,
    Math.max(MIN_QA_DURATION_MINUTES, Math.round(minutes)),
  );
}

/** Laptop-clock end of a Q&A pause. A 60-minute pause is 60 minutes, not 30. */
export function qaPauseEndsAtMs(nowMs: number, durationMinutes: number): number {
  return nowMs + clampQaDurationMinutes(durationMinutes) * 60_000;
}

export function isQaPauseOver(nowMs: number, endsAtMs: number): boolean {
  return nowMs >= endsAtMs;
}

export function loadQaWindow(sessionId: string): SessionQaWindow {
  const existing = loadAllQaWindows()[sessionId];
  return (
    existing ?? {
      sessionId,
      durationMinutes: DEFAULT_QA_DURATION_MINUTES,
      startedAt: null,
      endsAt: null,
      isActive: false,
    }
  );
}

export function setQaDurationForSession(sessionId: string, durationMinutes: number): void {
  const windows = loadAllQaWindows();
  const current = loadQaWindow(sessionId);
  windows[sessionId] = {
    ...current,
    durationMinutes: clampQaDurationMinutes(durationMinutes),
  };
  saveAllQaWindows(windows);
}
